import React, { useState, KeyboardEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from './Badge';
import clsx from 'clsx';

interface TagInputProps {
  id: string;
  label: string;
  placeholder?: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  description?: string;
  disabled?: boolean;
}

const TagInput: React.FC<TagInputProps> = ({ id, label, placeholder, tags, onChange, description, disabled }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim() !== '') {
      e.preventDefault();
      const tagValue = inputValue.trim().replace(/,\s*$/, '');
      if (tagValue && !tags.includes(tagValue)) {
        onChange([...tags, tagValue]);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      e.preventDefault();
      onChange(tags.slice(0, -1));
    }
  };

  const handleRemoveTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  return (
    <div className="flex-1">
      {label && (
        <label htmlFor={id} className="block text-xs text-gray-700 dark:text-gray-300 mb-2">
          {t(label)}
        </label>
      )}
      {description && (
        <p className="text-sm text-gray-700 dark:text-gray-200 text-left mb-1">{t(description)}</p>
      )}
      <div
        className={clsx(
          'flex items-center flex-wrap gap-1 bg-white dark:bg-gray-900 rounded-md shadow-sm px-3 py-2 min-h-[38px]',
          'focus-within:ring-1 focus-within:ring-primary-500 dark:focus-within:ring-primary-700',
          'transition duration-300',
          disabled && 'opacity-60 cursor-not-allowed'
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {tags.map((tag) => (
          <Badge
            key={tag}
            color="blue"
            onClose={disabled ? undefined : () => handleRemoveTag(tag)}
          >
            {tag}
          </Badge>
        ))}
        <input
          id={id}
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[120px] bg-transparent border-none focus:ring-0 focus:outline-none dark:text-white text-xs placeholder-gray-400 p-0"
          placeholder={tags.length === 0 ? (placeholder || t('Press Enter to add tags')) : ''}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default TagInput;
